import React, {useEffect, useState} from "react";
import {useTheme} from "@mui/material/styles";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {useNavigate, useLocation} from "react-router-dom";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MDInput from "components/MDInput";
import {
  Alert,
  AppBar,
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Typography
} from "@mui/material";
import MDButton from "components/MDButton";
import {GET, POST} from "APIclient";
import {useMaterialUIController} from "context";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {DemoContainer} from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {DataGrid} from "@mui/x-data-grid";
import properties from "constants/ApplicationConstants";
import {formatDesignation} from "constants/ApplicationConstants";
import {formatDate} from "constants/ApplicationConstants";

function SalaryRevision() {
  const [controller] = useMaterialUIController();
  const {darkMode} = controller;
  const location = useLocation();
  const [usersState] = useState(location.state);
  const theme = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [snakColor, setSnakColor] = useState("");
  const [snakMessage, setSnakMessage] = useState("");
  const fetchUrl = `admin/get-ctc/${usersState}`;
  const viewUrl = `admin/get-salaries/${usersState}`;
  const [details, setDetails] = useState({});
  const [data, setData] = useState([]);
  const [hrManager, setHrManager] = useState([]);
  const [designationList, setDesignationList] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSaveButtonEnabled, setIsSaveButtonEnabled] = useState(false);

  const [formData, setFormData] = useState({
    fromDate: null,
    toDate: null,
    designation: "",
    salary: "",
    issuedBy: "",
    variableAmount: ""
  });

  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "fromDate":
        if (!value || !dayjs(value).isValid()) {
          error = "Applicable From date is required";
        }
        break;
      case "toDate":
        if (value && !dayjs(value).isValid()) {
          error = "Invalid To Date";
        } else if (value && dayjs(value).isBefore(dayjs(formData.fromDate))) {
          error = "To Date cannot be before Applicable From date";
        }
        break;
      case "designation":
        if (!value) {
          error = "New Designation is required";
        }
        break;
      case "issuedBy":
        if (!value) {
          error = "Issued By is required";
        }
        break;
      case "salary":
        if (value && isNaN(value)) {
          error = "CTC must be a valid number";
        }
        break;
      case "variableAmount":
        if (value && isNaN(value)) {
          error = "Variable Pay Amount must be a valid number";
        }
        break;
      default:
        break;
    }

    return error;
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = event => {
    const {name, value} = event.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));

    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleDateChange = (date, field) => {
    const validDate = date ? dayjs(date) : null;
    const formattedDate = validDate?.isValid() ? validDate.format("YYYY-MM-DD") : null;

    setFormData(prev => ({
      ...prev,
      [field]: formattedDate
    }));

    const error = validateField(field, formattedDate);
    setErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const dt = {
      salary: formData.salary ? parseFloat(formData.salary) : 0,
      fromDate: formData.fromDate,
      variableAmount: formData.variableAmount ? parseFloat(formData.variableAmount) : 0,
      variablePayDate: formData.toDate,
      issuedBy: formData.issuedBy ? parseInt(formData.issuedBy) : null,
      designation: formData.designation,
      oldDesignation: details?.designation
    };

    const url = `admin/salary/add/${usersState}`;

    try {
      const response = await POST(url, dt);
      if (response?.data?.statusCode === 200) {
        setSnakColor("success");
        setSnakMessage("Salary Revision updated successfully");
        setOpen(true);
        setFormData({
          fromDate: null,
          toDate: null,
          designation: "",
          salary: "",
          issuedBy: "",
          variableAmount: ""
        });
        setErrors({});

        try {
          const ctcResponse = await GET(fetchUrl);
          const ctcData = ctcResponse?.data?.data || [];
          const indexedData = ctcData.map((item, index) => ({
            ...item,
            index: index + 1
          }));
          setData(indexedData);

          const detailsResponse = await GET(viewUrl);
          const viewData = detailsResponse?.data?.data;
          setDetails(viewData);
          setFormData(prev => ({
            ...prev,
            designation: viewData.designation || "",
            salary: viewData.salary ? viewData.salary.toString() : "",
            issuedBy: ""
          }));
        } catch (error) {
          console.error("Error fetching data:", error);
          setSnakColor("error");
          setSnakMessage("Error fetching data after submission");
          setOpen(true);
        }
      } else {
        setSnakColor("error");
        setSnakMessage(response?.data?.message || "Failed to update Salary Revision");
        setOpen(true);
      }
    } catch (error) {
      console.error("Error updating Salary Revision:", error);
      setSnakColor("error");
      setSnakMessage("Error updating Salary Revision");
      setOpen(true);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await GET(fetchUrl);
        const rawData = response?.data?.data || [];
        const indexedData = rawData.map((item, index) => ({
          ...item,
          index: index + 1
        }));
        setData(indexedData);
        // Use the latest salary record to populate variableAmount
        const latestRecord =
          indexedData && indexedData.length > 0 ? indexedData[indexedData.length - 1] : {};
        setFormData(prev => ({
          ...prev,
          variableAmount: latestRecord.variableAmount ? latestRecord.variableAmount.toString() : "",
          fromDate: dayjs(),
          toDate: dayjs()
        }));
      } catch (error) {
        console.error("Error fetching CTC data:", error);
        setSnakColor("error");
        setSnakMessage("Error fetching CTC data");
        setOpen(true);
      }
    };

    const fetchDetails = async () => {
      try {
        const response = await GET(viewUrl);
        const view = response?.data?.data;
        setDetails(view);
        setFormData(prev => ({
          ...prev,
          designation: view.designation || "",
          salary: view.salary ? view.salary.toString() : "",
          issuedBy: ""
        }));
      } catch (error) {
        console.error("Error fetching salary details:", error);
        setSnakColor("error");
        setSnakMessage("Error fetching salary details");
        setOpen(true);
      }
    };

    fetchData();
    fetchDetails();
  }, [fetchUrl, viewUrl]);

  useEffect(() => {
    const getUrl = `admin/list-designation`;
    const fetchDesignationList = async () => {
      try {
        const response = await GET(getUrl);
        const rawList = response?.data?.data;
        const formattedList = rawList.map(item => {
          let value = "";
          const designation = item.designationName.toLowerCase();
          if (designation === "manager - hr") {
            value = "manager_hr";
          } else if (designation === "operations manager") {
            value = "operations_manager";
          } else if (designation === "senior project manager") {
            value = "senior_project_manager";
          } else if (designation === "hr executive") {
            value = "hr_executive";
          } else if (designation === "qa engineer") {
            value = "qa_engineer";
          } else {
            value = designation.replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
          }
          return {
            label: item.designationName,
            value: value
          };
        });
        setDesignationList(formattedList);
      } catch (error) {
        console.error("Error fetching designation list:", error);
      }
    };
    fetchDesignationList();
  }, []);

  useEffect(() => {
    const getUrl = `admin/hr-managers`;
    const getHrManager = async () => {
      try {
        const response = await GET(getUrl);
        const data = response?.data?.data;
        setHrManager(data);
      } catch (error) {
        console.error("Error fetching HR managers:", error);
      }
    };
    getHrManager();
  }, []);

  const columns = [
    {
      field: "index",
      headerName: "#",
      width: 40,
      headerAlign: "center",
      align: "center"
    },
    {
      field: "fromDate",
      headerName: "From Date",
      width: 120,
      headerAlign: "center",
      align: "center",
      renderCell: params => formatDate(params.value) || "N/A"
    },
    {
      field: "toDate",
      headerName: "To Date",
      width: 120,
      headerAlign: "center",
      align: "center",
      renderCell: params => formatDate(params.value) || "N/A"
    },
    {
      field: "salary",
      headerName: "CTC",
      width: 160,
      headerAlign: "center",
      align: "center",
      renderCell: params => (params.value ? params.value.toFixed(2) : "0.00")
    },
    {
      field: "variableAmount",
      headerName: "Bonus",
      width: 160,
      headerAlign: "center",
      align: "center",
      renderCell: params => (params.value ? params.value.toFixed(2) : "0.00")
    },
    {
      field: "variablePayDate",
      headerName: "Bonus Pay Date",
      width: 140,
      headerAlign: "center",
      align: "center",
      renderCell: params => formatDate(params.value) || "N/A"
    },
    {
      field: "designation",
      headerName: "Designation",
      width: 200,
      headerAlign: "center",
      align: "center",
      renderCell: params => formatDesignation(params.value) || "N/A"
    }
  ];

  useEffect(() => {
    const requiredFieldsFilled = ["fromDate", "designation", "issuedBy"].every(
      key => formData[key] !== "" && formData[key] !== null && formData[key] !== undefined
    );
    const noErrors = Object.values(errors).every(err => !err);
    setIsSaveButtonEnabled(requiredFieldsFilled && noErrors);
  }, [formData, errors]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox container>
        <AppBar position="static">
          <Card>
            <MDBox
              pt={2}
              mb={0}
              pb={4}
              px={4}
              sx={{borderRadius: "5px", border: "1px solid #D3D3D3"}}
            >
              <Typography
                sx={{
                  color: "textPrimary",
                  fontFamily: properties.fontFamily,
                  fontSize: "16px",
                  fontWeight: "bold"
                }}
              >
                Salary Revision
              </Typography>
              <MDBox pt={1} px={2} mb={2}></MDBox>
              <MDBox
                component="li"
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                bgColor={darkMode ? "transparent" : "grey-100"}
                borderRadius="lg"
                p={2}
                mt={1}
              >
                <MDBox width="100%" display="flex" flexDirection="column">
                  <MDBox
                    display="grid"
                    gridTemplateColumns={{xs: "1fr", sm: "repeat(3, 1fr)"}}
                    gap={2}
                    mb={2}
                  >
                    <MDBox display="flex" alignItems="center">
                      <MDTypography
                        variant="caption"
                        color="text"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                      >
                        Employee ID:
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                      >
                        {details?.empId || "-"}
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" alignItems="center">
                      <MDTypography
                        variant="caption"
                        color="text"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                      >
                        Joining Date:
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                      >
                        {details?.joinDate ? formatDate(details.joinDate) : "-"}
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" alignItems="center">
                      <MDTypography
                        variant="caption"
                        color="text"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                      >
                        Designation:
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                      >
                        {formatDesignation(details?.designation || "-")}
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  <MDBox
                    display="grid"
                    gridTemplateColumns={{xs: "1fr", sm: "repeat(3, 1fr)"}}
                    gap={2}
                    mb={2}
                  >
                    <MDBox display="flex" alignItems="center">
                      <MDTypography
                        variant="caption"
                        color="text"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                      >
                        First Name:
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                      >
                        {details?.firstName || "-"}
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" alignItems="center">
                      <MDTypography
                        variant="caption"
                        color="text"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                      >
                        Middle Name:
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                      >
                        {details?.middleName || "-"}
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" alignItems="center">
                      <MDTypography
                        variant="caption"
                        color="text"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                      >
                        Last Name:
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                      >
                        {details?.lastName || "-"}
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  <MDBox
                    display="grid"
                    gridTemplateColumns={{xs: "1fr", sm: "repeat(3, 1fr)"}}
                    gap={2}
                    mb={2}
                  >
                    <MDBox display="flex" alignItems="center">
                      <MDTypography
                        variant="caption"
                        color="text"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                      >
                        Personal Email ID:
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                      >
                        {details?.emailAddress || "-"}
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" alignItems="center">
                      <MDTypography
                        variant="caption"
                        color="text"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                      >
                        Official Email ID:
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                      >
                        {details?.officialEmailAddress || "-"}
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" alignItems="center">
                      <MDTypography
                        variant="caption"
                        color="text"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                      >
                        Mobile:
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                      >
                        {details?.mobileNo || "-"}
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  <MDBox
                    display="grid"
                    gridTemplateColumns={{xs: "1fr", sm: "repeat(3, 1fr)"}}
                    gap={2}
                    mb={2}
                  >
                    <MDBox display="flex" alignItems="center">
                      <MDTypography
                        variant="caption"
                        color="text"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                      >
                        Current CTC:
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                      >
                        {details?.salary ? details.salary.toFixed(2) : "-"}
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" alignItems="center">
                      <MDTypography
                        variant="caption"
                        color="text"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                      >
                        Employee Type:
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                      >
                        {details?.empType || "-"}
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" alignItems="center">
                      <MDTypography
                        variant="caption"
                        color="text"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                      >
                        Applicable From:
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        fontFamily={properties.fontFamily}
                        sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                      >
                        {details?.fromDate ? formatDate(details.fromDate) : "-"}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </MDBox>
              <MDBox pb={1} sx={{overflow: "auto"}}>
                <DataGrid
                  rows={data}
                  columns={columns}
                  getRowId={row => row.salaryId}
                  pagination={false}
                  hideFooter={data.length <= 20}
                  hideFooterPagination={true}
                  sx={{
                    fontFamily: properties.fontFamily,
                    fontSize: "14px",
                    "& .MuiDataGrid-columnHeaders": {
                      position: "sticky",
                      top: 0,
                      backgroundColor: "white",
                      zIndex: 1000
                    },
                    "& .MuiDataGrid-virtualScroller": {
                      overflow: "auto"
                    },
                    "& .MuiDataGrid-root": {
                      height: "auto",
                      maxHeight: "600px"
                    }
                  }}
                />
              </MDBox>
              <MDBox pt={2} mb={0} pb={4} px={4}>
                <InputLabel
                  sx={{fontWeight: "700", paddingBottom: "20px", fontFamily: properties.fontFamily}}
                >
                  Salary Details
                </InputLabel>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={["DatePicker"]}>
                    <DatePicker
                      format="DD/MM/YYYY"
                      sx={{m: 1, width: "20ch"}}
                      size="small"
                      label={
                        <span>
                          Applicable From <span style={{color: properties.colors.Red}}>*</span>
                        </span>
                      }
                      name="fromDate"
                      value={formData ? dayjs(formData.fromDate) : dayjs()}
                      onChange={date => handleDateChange(date, "fromDate")}
                      slotProps={{
                        textField: {
                          error: !!errors.fromDate,
                          helperText: errors.fromDate
                        }
                      }}
                    />
                  </DemoContainer>
                </LocalizationProvider>

                <FormControl variant="standard" sx={{m: 1, width: "30ch"}}>
                  <InputLabel id="MasterTeam">
                    New Designation <span style={{color: properties.colors.Red}}> *</span>
                  </InputLabel>
                  <Select
                    value={formData.designation || ""}
                    name="designation"
                    label="New Designation"
                    onChange={e => handleChange(e)}
                    IconComponent={ExpandMoreIcon}
                    error={!!errors.designation}
                  >
                    <MenuItem value="">
                      <em>Select</em>
                    </MenuItem>
                    {designationList?.map(designation => (
                      <MenuItem key={designation.value} value={designation.value}>
                        {designation.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.designation && (
                    <MDTypography variant="caption" color="error">
                      {errors.designation}
                    </MDTypography>
                  )}
                </FormControl>

                <MDBox mb={2} display="flex" gap={2}>
                  <MDInput
                    type="text"
                    name="salary"
                    label={<span>CTC</span>}
                    value={formData.salary || ""}
                    onChange={e => handleChange(e)}
                    variant="standard"
                    sx={{m: 1, width: "30ch"}}
                    error={!!errors.salary}
                    helperText={errors.salary}
                  />
                  <MDInput
                    type="text"
                    name="variableAmount"
                    label={<span>Variable Pay Amount</span>}
                    value={formData.variableAmount || ""}
                    onChange={e => handleChange(e)}
                    variant="standard"
                    sx={{m: 1, width: "30ch"}}
                    error={!!errors.variableAmount}
                    helperText={errors.variableAmount}
                  />
                </MDBox>

                <FormControl variant="standard" sx={{m: 1, width: "30ch", paddingBottom: "20px"}}>
                  <InputLabel id="issuedBy" sx={{fontWeight: "bold"}}>
                    Issued By <span style={{color: properties.colors.Red}}> *</span>
                  </InputLabel>
                  <Select
                    labelId="issuedBy"
                    id="issuedBy"
                    value={formData.issuedBy || ""}
                    name="issuedBy"
                    onChange={e => handleChange(e)}
                    label="Issued By"
                    error={!!errors.issuedBy}
                  >
                    <MenuItem value="">
                      <em>Select</em>
                    </MenuItem>
                    {hrManager?.map(manager => (
                      <MenuItem key={manager.empId} value={manager.empId}>
                        {`${manager.firstName} ${manager.lastName}`}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.issuedBy && (
                    <MDTypography variant="caption" color="error">
                      {errors.issuedBy}
                    </MDTypography>
                  )}
                </FormControl>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={["DatePicker"]}>
                    <DatePicker
                      format="DD/MM/YYYY"
                      sx={{m: 1, width: "20ch"}}
                      size="small"
                      label={
                        <span>
                          To Date <span style={{color: properties.colors.Red}}> *</span>
                        </span>
                      }
                      name="toDate"
                      value={formData.toDate ? dayjs(formData.toDate) : dayjs()}
                      onChange={date => handleDateChange(date, "toDate")}
                      slotProps={{
                        textField: {
                          error: !!errors.toDate,
                          helperText: errors.toDate
                        }
                      }}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </MDBox>

              <MDBox mt={3} mb={2} sx={{display: "flex", justifyContent: "flex-end"}}>
                <MDBox sx={{display: "flex", gap: 1}}>
                  <MDButton
                    variant="gradient"
                    color="warning"
                    sx={{width: "20ch", fontFamily: properties.fontFamily}}
                    onClick={() => navigate(-1)}
                  >
                    Back
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="info"
                    sx={{
                      width: "20ch",
                      ml: 2,
                      mr: 1,
                      background: properties.colors.bgcolorbluethem,
                      fontFamily: properties.fontFamily
                    }}
                    onClick={() => handleSubmit()}
                    disabled={!isSaveButtonEnabled}
                  >
                    Save
                  </MDButton>
                </MDBox>
              </MDBox>

              <Snackbar
                open={open}
                anchorOrigin={{vertical: "top", horizontal: "right"}}
                autoHideDuration={1200}
                onClose={handleClose}
              >
                <Alert
                  onClose={handleClose}
                  severity={snakColor === "success" ? "success" : "error"}
                  variant="filled"
                  sx={{width: "100%", color: "#fff"}}
                >
                  {snakMessage}
                </Alert>
              </Snackbar>
            </MDBox>
          </Card>
        </AppBar>
      </MDBox>
    </DashboardLayout>
  );
}

export default SalaryRevision;
